import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { LogOut, User as UserIcon, Menu, ShieldCheck } from "lucide-react";
import NotificationBell from "@/components/NotificationBell";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ROLE_LABELS } from "@/lib/roles";

export default function Header({ title, subtitle, actions, onMenuClick }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <header
      className="sticky top-0 z-30 bg-gradient-to-r from-blue-700 to-slate-900 text-white shadow-md"
      data-testid="main-header"
    >
      <div className="px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={onMenuClick}
            className="lg:hidden p-2 rounded-lg hover:bg-white/10 transition-colors"
            data-testid="mobile-menu-btn"
          >
            <Menu size={20} />
          </button>
          <div className="min-w-0">
            <div className="text-[11px] tracking-[0.18em] uppercase text-white/60 font-semibold">
              Prest'Hyg Qualité
            </div>
            <h1
              className="text-xl sm:text-2xl font-bold tracking-tight truncate font-heading"
              data-testid="page-title"
            >
              {title}
            </h1>
            {subtitle ? (
              <div className="text-xs text-white/70 mt-0.5">{subtitle}</div>
            ) : null}
          </div>
        </div>

        <div className="flex items-center gap-3">
          {actions}
          <NotificationBell />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-3 pl-2 pr-3 py-1.5 rounded-full hover:bg-white/10 transition-colors"
                data-testid="user-menu-btn"
              >
                <div className="h-9 w-9 rounded-full bg-white/10 flex items-center justify-center overflow-hidden ring-2 ring-white/20">
                  <UserIcon size={16} />
                </div>
                <div className="hidden sm:block text-left">
                  <div className="text-sm font-semibold leading-tight">
                    {user?.name}
                  </div>
                  <div className="text-[11px] text-white/60 leading-tight">
                    {ROLE_LABELS[user?.role] || user?.role}
                  </div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="font-semibold">{user?.name}</div>
                <div className="text-xs text-slate-500 font-normal">
                  {user?.email}
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => navigate("/parametres")}
                data-testid="menu-settings"
              >
                <ShieldCheck size={14} className="mr-2" />
                Paramètres
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={async () => {
                  await logout();
                  navigate("/login");
                }}
                data-testid="menu-logout"
                className="text-rose-600 focus:text-rose-700"
              >
                <LogOut size={14} className="mr-2" />
                Déconnexion
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
